import React, { createContext, useCallback, useEffect } from 'react'
import Cookies from 'cookie-universal'
import { pickBy } from 'lodash'
import { useQuery } from '@tanstack/react-query'
// eslint-disable-next-line no-restricted-imports
import { getExperiments } from '@mc/experiments'
// eslint-disable-next-line no-restricted-imports
import { getGeo, getRegion } from '@mc/geo'
import { getCurrentLocale, getBrowserLocales } from '@mc/i18n'
import {
  CommerceGatewaySubscription,
  SubscriptionsService,
  User,
  UsersService,
} from '@mc/persona-api'
import { Auth0Service } from '../../services/Auth0Service'
import createEventId from '../../tracking/interactors/createEventId'
import createOrGetVisitSessionIdFromCookie from '../../tracking/interactors/createOrGetVisitSessionIdFromCookie'
import getAnonymousId, {
  ANONYMOUS_ID,
} from '../../tracking/interactors/getAnonymousId'
import getFacebookBrowserId from '../../tracking/interactors/getFacebookBrowserId'
import getFacebookClickId from '../../tracking/interactors/getFacebookClickId'
import { TRACK_PROPS } from '../../tracking/trackMediaConstants'
import { logSegmentCalls } from '../../tracking/utils'

type TContext = {
  user: { email: string; name: string; picture?: string } | undefined
  me: User | undefined
  isLoading: boolean
  subscription?: CommerceGatewaySubscription
  isLoadingMe: boolean
  isLoadingSubscription: boolean
  refetchSubscription: () => void
}

export const Auth0UserContext = createContext<TContext>({
  user: undefined,
  isLoading: false,
  me: undefined,
  isLoadingMe: false,
  isLoadingSubscription: false,
  refetchSubscription: () => {},
})

const WrapperMVP = ({ children }: { children: React.ReactNode }) => {
  // Needed API call to change from reservation entitlement to entitlement
  const { data: userMCMe, isLoading: isLoadingMe } = useQuery({
    queryFn: () => UsersService.getUser({ uuid: 'me' }),
    queryKey: ['me'],
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

  const { data: userMVP, isLoading: isLoadingMVP } = useQuery({
    queryFn: Auth0Service.userInfo,
    queryKey: ['userInfo'],
    retry: false,
    refetchOnWindowFocus: false,
  })

  const {
    data: subscription,
    failureCount,
    isFetched,
    refetch: refetchSubscription,
  } = useQuery({
    queryFn: () => SubscriptionsService.getSubscription({ id: 'me' }),
    queryKey: ['subscription'],
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

  const addFacebookIds = () => ({
    fbp: getFacebookBrowserId(),
    fbc: getFacebookClickId(),
  })
  const getUserExperiments = () => {
    const experiments = pickBy(getExperiments())
    return Object.entries(experiments).map(
      ([name, value]) => `${name}_${value}`,
    )
  }

  const identify = useCallback(
    (
      userId?: string | number | null,
      traits = {},
      options: SegmentAnalytics.SegmentOpts = {},
    ) => {
      const userTraits = {
        ...traits,
        experiments: getUserExperiments(),
        user_locale: getCurrentLocale(),
        native_locale: getBrowserLocales(),
        geo: getGeo(),
        region: getRegion(),
        event_id: createEventId(),
        [TRACK_PROPS.VISIT_SESSION_ID]: createOrGetVisitSessionIdFromCookie(),
        ...addFacebookIds(),
      }
      if (window.analytics) {
        const integrations = {
          Pardot: false,
          ...options.integrations,
        }
        // identify -- Pardot is off by default
        window.analytics.identify(
          // for backwards compatibility - we need to continue sending `null`
          (userId?.toString() ?? null) as string,
          userTraits,
          {
            ...options,
            integrations,
            vendor_integrations: integrations,
          } as SegmentAnalytics.SegmentOpts,
          () => {
            logSegmentCalls('identify', 'identify', userTraits)
          },
        )
      }
    },
    [],
  )

  useEffect(() => {
    const cookies = Cookies()
    const previousAnonymousId = getAnonymousId()
    const anonymousId = userMCMe?.analytics.anonymousId
    if (userMCMe) {
      const userTraits = {
        userId: userMCMe.analytics.userId,
        persona_user_id: userMCMe.analytics.personaUserId,
        user_plan_status: userMCMe.analytics.properties?.userPlanStatus,
        anonymous_id: previousAnonymousId,
      }
      cookies.set(ANONYMOUS_ID, anonymousId)
      identify(userMCMe.analytics.userId, userTraits)
    }
  }, [userMCMe, identify])

  const subscriptionLoaded = failureCount > 0 || isFetched

  return (
    <Auth0UserContext.Provider
      value={{
        user: userMVP,
        isLoading: isLoadingMVP,
        me: userMCMe,
        subscription,
        isLoadingMe,
        isLoadingSubscription: !subscriptionLoaded,
        refetchSubscription,
      }}
    >
      {children}
    </Auth0UserContext.Provider>
  )
}

export const Auth0UserProvider = ({
  children,
}: {
  children: React.ReactNode
}) => <WrapperMVP>{children}</WrapperMVP>
